import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { PostStatus } from '@repo/shared-types';
import type { AdminAuthorInput, AdminCreatePostInput, AdminUpdatePostInput } from '@repo/validation';
import { adminFetchApi } from '../lib/api';

/** Mirrors the Prisma `Post`/`Author` models' JSON shape as returned by
 *  `/admin/blog/{posts,authors}`. `@repo/shared-types/blog.ts` only defines
 *  the *public* DTOs (`BlogPostSummary`/`BlogPostDetail`/`BlogAuthorSummary`),
 *  which hand-build a narrower shape and deliberately drop fields (`id`,
 *  `status`, `authorId`, `persona`, …) that must never reach the public API —
 *  see docs/design/004-Blog-Design.md §4/§7. The admin surface needs those
 *  fields, so they're kept local to this app rather than added to the public
 *  DTOs. */
export interface AdminBlogAuthor {
  id: string;
  slug: string;
  name: string;
  role: string | null;
  bio: string | null;
  avatarUrl: string | null;
  /** Admin-only briefing note on this author's writing voice. Never rendered
   *  publicly — used here only to assemble the "copy AI prompt" text. */
  persona: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  bodyMd: string;
  coverUrl: string | null;
  tags: string[];
  status: PostStatus;
  publishedAt: string | null;
  authorId: string;
  author: AdminBlogAuthor;
  createdAt: string;
  updatedAt: string;
}

export function useAdminBlogPosts() {
  return useQuery({
    queryKey: ['admin', 'blog', 'posts'],
    queryFn: () => adminFetchApi<AdminBlogPost[]>('/admin/blog/posts'),
  });
}

export function useAdminBlogPost(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'blog', 'posts', id],
    queryFn: () => adminFetchApi<AdminBlogPost>(`/admin/blog/posts/${id}`),
    enabled: !!id,
  });
}

export function useAdminBlogAuthors() {
  return useQuery({
    queryKey: ['admin', 'blog', 'authors'],
    queryFn: () => adminFetchApi<AdminBlogAuthor[]>('/admin/blog/authors'),
  });
}

export function useCreateBlogAuthor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminAuthorInput) =>
      adminFetchApi<AdminBlogAuthor>('/admin/blog/authors', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog', 'authors'] });
      toast.success('Đã tạo tác giả');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** `id` is fixed per hook instance — safe because the sheet that owns this
 *  form remounts (keyed by author id) whenever it switches which author it
 *  is editing, same reasoning as `useUpdateBlogPost` above. */
export function useUpdateBlogAuthor(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AdminAuthorInput>) =>
      adminFetchApi<AdminBlogAuthor>(`/admin/blog/authors/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog', 'authors'] });
      toast.success('Đã lưu tác giả');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useCreateBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminCreatePostInput) =>
      adminFetchApi<AdminBlogPost>('/admin/blog/posts', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog', 'posts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** `id` is fixed per hook instance — safe because the editor page remounts
 *  (its route param changes) when navigating from one post to another. */
export function useUpdateBlogPost(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUpdatePostInput) =>
      adminFetchApi<AdminBlogPost>(`/admin/blog/posts/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: (post) => {
      qc.setQueryData(['admin', 'blog', 'posts', id], post);
      qc.invalidateQueries({ queryKey: ['admin', 'blog', 'posts'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminFetchApi(`/admin/blog/posts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'blog', 'posts'] });
      toast.success('Đã xoá bài viết');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** Stateless preview: `{ bodyMd }` -> `{ html }`. Not keyed by post id and
 *  writes nothing server-side — see docs/design/004-Blog-Design.md §4. */
export function useBlogPreview() {
  return useMutation({
    mutationFn: (bodyMd: string) =>
      adminFetchApi<{ html: string }>('/admin/blog/posts/preview', {
        method: 'POST',
        body: JSON.stringify({ bodyMd }),
      }),
  });
}

export function useUploadBlogMedia() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return adminFetchApi<{ url: string }>('/admin/blog/media', { method: 'POST', body: form });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** `<input type="datetime-local">` carries NO timezone — its value is
 *  wall-clock time in whatever zone the browser happens to be set to.
 *  Convert through a `Date` in both directions; slicing the ISO string
 *  instead would shift the value by the UTC offset. See
 *  docs/design/004-Blog-Design.md §3. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** The clock behind the derived visibility chip and the Đăng/Đặt lịch label.
 *  React's purity rule rejects `Date.now()` during render; putting the clock
 *  in state and ticking it also makes a scheduled post flip to `live` on its
 *  own instead of lying until someone reloads the page. */
export function useNow(intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
